import type { z } from "zod";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { getDiscoveryEventEmitter } from "@/lib/core/discovery-core.server";
import {
    captureSearchSubmission,
    commitSearchSubmission,
} from "./search-submission-posthog";
import { searchTelemetryInputSchema } from "./search-telemetry.contract.ts";

const getServerDiscoveryTelemetry = createServerOnlyFn(
    async () => await getDiscoveryEventEmitter(),
);

const trackSearchTelemetryServer = createServerFn({ method: "POST" })
    .inputValidator((input) => searchTelemetryInputSchema.parse(input))
    .handler(async ({ data }) => {
        const { context } = data;
        const telemetry = (await getServerDiscoveryTelemetry()).with({
            searchInteractionId: context.searchInteractionId,
            submissionId: context.submissionId,
            queryRevision: String(context.queryRevision),
            ...(context.parseId ? { parseId: context.parseId } : {}),
        });

        if (data.type === "submitted") {
            telemetry.emit("discovery.search.submitted", {
                searchInteractionId: context.searchInteractionId,
                submissionId: context.submissionId,
                queryRevision: context.queryRevision,
                parseId: context.parseId,
                submissionSource: context.submissionSource,
                action: context.action,
                surface: context.surface,
            });
            return;
        }

        if (data.type === "results_presented") {
            telemetry.emit("discovery.search.results_presented", {
                searchInteractionId: context.searchInteractionId,
                submissionId: context.submissionId,
                presentationId: data.presentationId,
                batchIndex: data.batchIndex,
                executionId: data.executionId,
                shownEntities: data.shownEntities,
                totalAvailableCount: data.totalAvailableCount,
                activeSection: data.activeSection,
                prefetchAttempts: data.prefetchAttempts,
                prefetchAttemptsDropped: data.prefetchAttemptsDropped,
                displayedResultCount: data.displayedResultCount,
                listingDetailsHydratedCount: data.listingDetailsHydratedCount,
            });
            return;
        }

        if (data.type === "terminal_option_selected") {
            telemetry.emit("discovery.search.terminal_option_selected", {
                searchInteractionId: context.searchInteractionId,
                submissionId: context.submissionId,
                queryRevision: context.queryRevision,
                parseId: context.parseId,
                selectionId: data.selectionId,
                option: data.option,
            });
            return;
        }

        telemetry.emit("discovery.search.result_selected", {
            searchInteractionId: context.searchInteractionId,
            submissionId: context.submissionId,
            presentationId: data.presentationId,
            selectionId: data.selectionId,
            entityType: data.entityType,
            entityId: data.entityId,
            rank: data.rank,
            intentIndexes: data.intentIndexes,
            action: data.action,
            listingDetailsHydrated: data.listingDetailsHydrated,
        });
    });

export const trackSearchTelemetry = trackSearchTelemetryServer;

type SubmittedTelemetry = Extract<
    z.infer<typeof searchTelemetryInputSchema>,
    { type: "submitted" }
>;

export function trackSearchSubmission(
    draft: Omit<SubmittedTelemetry["context"], "submissionId">,
): SubmittedTelemetry["context"] {
    const context = commitSearchSubmission(draft);
    void trackSearchTelemetryServer({
        data: { type: "submitted", context },
    }).catch(() => {});
    return context;
}

export function trackSearchUrlNavigation(
    context: SubmittedTelemetry["context"],
): void {
    captureSearchSubmission(context);
    void trackSearchTelemetryServer({
        data: { type: "submitted", context },
    }).catch(() => {});
}
